import { useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography } from '../../components/ui';
import { useKnowledgeBase } from '../../contexts/KnowledgeBaseContext';
import { useTheme } from '../../contexts/ThemeContext';
import { formatTimeAgo } from '../../utils/dateUtils';
import { ARTICLE_TYPE_LABELS } from '../../types/knowledgeBase';
import { StepCard } from './components/StepCard';
import { KnownErrorView } from './components/KnownErrorView';
import { SOPRunbookView } from './components/SOPRunbookView';
import './ArticleView.css';

// Current user role - hardcoded for now
const CURRENT_USER_ROLE = 'T2 Tech';

function canEditArticles(role: string): boolean {
  return role === 'Admin' || role === 'T2 Tech';
}

export function ArticleView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { getArticleById, incrementViewCount } = useKnowledgeBase();

  const article = id ? getArticleById(id) : undefined;

  // Increment view count on mount
  useEffect(() => {
    if (id) {
      incrementViewCount(id);
    }
  }, [id, incrementViewCount]);

  const handleBack = useCallback(() => {
    navigate('/knowledge-base');
  }, [navigate]);

  const handleEdit = useCallback(() => {
    if (id) {
      navigate(`/knowledge-base/edit/${id}`);
    }
  }, [navigate, id]);

  if (!article) {
    return (
      <Box className="article-view" data-theme={theme}>
        <Box className="article-view__container">
          <Box className="article-view__not-found">
            <Typography variant="h5">Article not found</Typography>
            <Typography variant="body2" color="text.secondary">
              The article you are looking for does not exist or has been removed.
            </Typography>
            <button className="article-view__back-button" onClick={handleBack}>
              Back to Knowledge Base
            </button>
          </Box>
        </Box>
      </Box>
    );
  }

  const showEditButton = canEditArticles(CURRENT_USER_ROLE);

  return (
    <Box className="article-view" data-theme={theme}>
      <Box className="article-view__container">
        {/* Header */}
        <Box className="article-view__header">
          <button className="article-view__back-link" onClick={handleBack} type="button">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M10 12L6 8l4-4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Back to Knowledge Base
          </button>

          {showEditButton && (
            <button className="article-view__edit-button" onClick={handleEdit} type="button">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M11.5 2.5l2 2-8 8H3.5v-2l8-8z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Edit
            </button>
          )}
        </Box>

        {/* Article Meta */}
        <Box className="article-view__meta">
          <Box className="article-view__badges">
            <span className="article-view__category">{article.category}</span>
            <span className={`article-view__type article-view__type--${article.articleType}`}>
              {ARTICLE_TYPE_LABELS[article.articleType]}
            </span>
          </Box>
          <Typography variant="h4" component="h1" className="article-view__title">
            {article.title}
          </Typography>
          <Typography variant="body1" className="article-view__description">
            {article.description}
          </Typography>

          <Box className="article-view__info">
            <Box className="article-view__tags" role="list" aria-label="Tags">
              {article.tags.map((tag) => (
                <span key={tag} className="article-view__tag" role="listitem">
                  {tag}
                </span>
              ))}
            </Box>
            <Box className="article-view__audiences" role="list" aria-label="Target audiences">
              {article.audiences.map((audience) => (
                <span key={audience} className="article-view__audience" role="listitem">
                  {audience}
                </span>
              ))}
            </Box>
            <Box className="article-view__author">
              <span>By {article.authorName}</span>
              <span className="article-view__separator" aria-hidden="true">|</span>
              <span>Updated {formatTimeAgo(article.updatedAt)}</span>
              <span className="article-view__separator" aria-hidden="true">|</span>
              <span>{article.viewCount} views</span>
            </Box>
          </Box>
        </Box>

        {/* Content based on article type */}
        {article.articleContent.type === 'how-to' && (
          <Box className="article-view__steps" role="list" aria-label="Steps">
            {article.articleContent.content.steps.map((step) => (
              <StepCard key={step.id} step={step} />
            ))}
          </Box>
        )}

        {article.articleContent.type === 'known-error' && (
          <KnownErrorView content={article.articleContent.content} />
        )}

        {article.articleContent.type === 'sop-runbook' && (
          <SOPRunbookView content={article.articleContent.content} />
        )}
      </Box>
    </Box>
  );
}
